// ============================================================================
// TrimaRankingMain — Orchestrator of the 24-question ranking phase
// ============================================================================
// State lives entirely in memory (no persistence). On unmount or refresh, the
// user loses all in-progress rankings and restarts from the invitation screen.
// This is intentional, per the brief.
//
// Flow:
//   1. Load 24 questions on mount (already done by parent — passed via props)
//   2. Show one question at a time
//   3. On Validate → store response in memory, advance to next
//   4. On last question → POST all 24×5 responses, then call onComplete
// ============================================================================

import React, { useState, useRef } from 'react';
import { useIonLoading } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import useAPITrima from 'hooks/api/trima/useAPITrima';
import TrimaProgressHeader from 'pages/questionnaire-trima/components/TrimaProgressHeader';
import TrimaRankingQuestion from 'pages/questionnaire-trima/slides/ranking/TrimaRankingQuestion';
import TrimaRankingOnboarding from 'pages/questionnaire-trima/slides/ranking/TrimaRankingOnboarding';
import { TrimaRankingMainWrapper } from 'pages/questionnaire-trima/slides/ranking/TrimaRankingMain.styled';
import {
  TrimaRankingPayload,
  TrimaRankingResponse,
  TrimaCadranPayload,
} from 'pages/questionnaire-trima/types/trima.types';

export interface TrimaRankingMainProps {
  payload: TrimaRankingPayload;
  /** Called when all 24 questions have been answered and POSTed successfully. */
  onComplete: (firstCadranPayload: TrimaCadranPayload) => void;
  /** Called if the POST of the 24×5 responses fails. */
  onError?: () => void;
}

const TrimaRankingMain: React.FC<TrimaRankingMainProps> = ({
  payload,
  onComplete,
  onError,
}) => {
  const { t } = useTranslation('trima');
  const { postRanking } = useAPITrima();
  const [present, dismiss] = useIonLoading();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const responsesRef = useRef<TrimaRankingResponse[]>([]);

  const totalQuestions = payload.questions.length;
  const currentQuestion = payload.questions[currentIndex];

  const handleValidate = async (response: TrimaRankingResponse) => {
    // Upsert: replace any prior response for this question (in case the user
    // somehow re-answers — defensive coding for future bidi navigation).
    const existing = responsesRef.current.findIndex(
      (r) => r.questionId === response.questionId,
    );
    if (existing >= 0) {
      responsesRef.current[existing] = response;
    } else {
      responsesRef.current.push(response);
    }

    const isLastQuestion = currentIndex >= totalQuestions - 1;

    if (!isLastQuestion) {
      setCurrentIndex((i) => i + 1);
      return;
    }

    // Last question: POST everything in one shot.
    await present({ spinner: 'circles', message: t('cadran.loading-next') });
    const firstCadran = await postRanking(payload.questionnaireId, responsesRef.current);
    await dismiss();

    if (firstCadran) {
      onComplete(firstCadran);
    } else {
      onError?.();
    }
  };

  // Global progress: ranking phase occupies the first 1/5 of the overall flow.
  // Within that, each question contributes 1/24 of the phase. So the percent
  // returned by this component is:
  //    (currentIndex / totalQuestions) * (100 / 5)
  // But TrimaProgressHeader expects 0..100 of the global flow, so:
  const progressPercent = (currentIndex / totalQuestions) * (100 / 5);

  return (
    <TrimaRankingMainWrapper>
      <TrimaProgressHeader
        currentStep={1}
        progressPercent={progressPercent}
        localCounterLabel={t('ranking.question-counter', {
          current: currentIndex + 1,
          total: totalQuestions,
        })}
      />

      <TrimaRankingQuestion
        key={currentQuestion.id}
        question={currentQuestion}
        questionIndex={currentIndex}
        totalQuestions={totalQuestions}
        onValidate={handleValidate}
      />

      {showOnboarding && (
        <TrimaRankingOnboarding
          totalQuestions={totalQuestions}
          onDismiss={() => setShowOnboarding(false)}
        />
      )}
    </TrimaRankingMainWrapper>
  );
};

export default TrimaRankingMain;
